import { globalRef, isSilentError, type Maybe } from '@amazon/vinyl-util'
import { type CloudQueueController, mrnMediaType, type PlaylistController } from '@amzn/bf-playback'
import { QueueEntityUnavailableError } from '@amzn/bf-playback/cloudQueueService'
import { toastPlaybackFailed } from '@/playback/playbackNoticeToast'

export interface PlaybackStartFailureDeps {
    readonly playlistController: Pick<PlaylistController, 'clear'>
    readonly cloudQueueController: Pick<CloudQueueController, 'clearRestoreSnapshot'>
}

export interface QueueStartDeps extends PlaybackStartFailureDeps {
    readonly cloudQueueController: Pick<CloudQueueController, 'clearRestoreSnapshot' | 'setQueue'>
}

/** The play awaiting its first audio, with its content type (null when the id names none). */
const startingRef = globalRef(() => ({ current: null as { mediaType: string | null } | null }))

/** A press began playing new content: a failure before audio starts names this content type. */
export function beginPlaybackStart(playParamsId: Maybe<string>): void {
    startingRef.value.current = { mediaType: mrnMediaType(playParamsId) }
}

/** Audio started, or the start was abandoned. */
export function endPlaybackStart(): void {
    startingRef.value.current = null
}

/** Settles a start still awaiting audio as failed; null when no start is pending. */
export function takeFailedPlaybackStart(): { mediaType: string | null } | null {
    const { current } = startingRef.value
    endPlaybackStart()
    return current
}

/** Empties the player and forgets the queue a reload would restore. */
export function clearFailedPlayback(deps: PlaybackStartFailureDeps): void {
    deps.playlistController.clear()
    deps.cloudQueueController.clearRestoreSnapshot()
}

/** A play that cannot start toasts which kind of content failed and clears the player. */
export function failPlaybackStart(
    error: unknown,
    playParamsId: Maybe<string>,
    deps: PlaybackStartFailureDeps,
    errorHandler: (error: Error) => void
): void {
    endPlaybackStart()
    if (error instanceof QueueEntityUnavailableError) {
        clearFailedPlayback(deps)
        return
    }
    if (isSilentError(error)) return
    toastPlaybackFailed(mrnMediaType(playParamsId))
    clearFailedPlayback(deps)
    errorHandler(error as Error)
}
